import { inject, Injectable } from '@angular/core';
import { formatDate } from '@angular/common';
import C2S from 'canvas2svg';
import { PdfGeneratorService } from 'src/app/modules/viewer/service/pdfgenerator-service';
import { PdfGeneratorCorteService } from 'src/app/modules/viewer/service/pdfgenerator-corte-service';
import { abbreviateMaterial } from 'src/app/modules/viewer/utils/utils';

export interface ExportCurvePoint {
  id?: string;
  x: number;
  y: number;
  xFixed?: number;
  blockY?: boolean;
  controlPoint1X?: number;
  controlPoint1Y?: number;
  controlPoint2X?: number;
  controlPoint2Y?: number;
  linkedControlPoints?: boolean;
  isSmooth?: boolean;
}

interface Point {
  x: number;
  y: number;
}

interface TabCornerPoints {
  start: Point;
  end: Point;
  startOut: Point;
  endOut: Point;
  // Esquinas EXTERNAS reales del trapecio de la solapa (borde interno start/end
  // desplazado por la profundidad tabDepthPx y encogido por tabOuterShrinkRatio).
  // Necesarias para ubicar el cartel: sin ellas createSolapaText reconstruía el
  // fondo con offsets hardcodeados y el texto quedaba desubicado.
  outerStart: Point;
  outerEnd: Point;
}

export interface CurveDataLayer {
  name: string;
  initialCurvePoints: ExportCurvePoint[];
  originalCurvePoints: ExportCurvePoint[];
  initialCurvePointsFlexed: ExportCurvePoint[];
  originalCurvePointsFlexed: ExportCurvePoint[];
  leftMMFlexed: number;
  rightMMFlexed: number;
}

export interface CurveDataJson {
  version?: string;
  canvasDimensions?: { width: number; height: number };
  dicomDimensions?: { col: number; row: number };
  conversionFactors: {
    pixelesConvertFactorCol: number;
    pixelesConvertFactorRow: number;
  };
  flexValues?: { leftMMFlexed: number; rightMMFlexed: number };
  layers: CurveDataLayer[];
}

export interface ImplantPdfImplantInfo {
  fullname: string;
  surgeryLocation: string;
  surgeryDay: string;
  material: string;
}

export interface ImplantPdfInput {
  curveData: CurveDataJson;
  implantInfo: ImplantPdfImplantInfo;
  procedure_id: number;
  nussTemplateVariant: 'v1' | 'v2';
}

interface IProcedureTemplate {
  id: number;
  templateUrl: string;
}

@Injectable({ providedIn: 'root' })
export class ImplantPdfExportService {
  private pdfGeneratorService = inject(PdfGeneratorService);
  private pdfGeneratorCorteService = inject(PdfGeneratorCorteService);

  async generatePlano(input: ImplantPdfInput): Promise<void> {
    const { curveData, implantInfo, procedure_id, nussTemplateVariant } = input;
    const pxToMm = curveData.conversionFactors.pixelesConvertFactorCol;
    const pixelesConvertFactorCol = curveData.conversionFactors.pixelesConvertFactorCol;
    const pixelesConvertFactorRow = curveData.conversionFactors.pixelesConvertFactorRow;

    this.pdfGeneratorService.clearCurves();

    for (const layer of curveData.layers) {
      if (!layer.initialCurvePoints || layer.initialCurvePoints.length === 0) continue;

      const flLengthPx = this.calculateBezierCurveLength(layer.initialCurvePointsFlexed);
      const flLengthMm = this.convertPixelsToMillimeters(flLengthPx, pxToMm);
      const implantLabel = this.getImplantTypeLabel(layer.name);
      const fullName = implantLabel
        ? `${implantInfo.fullname} - ${implantLabel}`
        : implantInfo.fullname;

      this.exportCurve({
        curvePoints: layer.initialCurvePointsFlexed,
        curveColor: 'green',
        curveTextColor: 'black',
        canvasPaddingMm: 0,
        lines: [
          fullName,
          implantInfo.surgeryLocation,
          implantInfo.surgeryDay,
          `${abbreviateMaterial(implantInfo.material)} : ${Number(flLengthMm).toFixed(2)} mm : FL ${layer.leftMMFlexed}/${layer.rightMMFlexed} mm`,
        ],
        pdfService: this.pdfGeneratorService,
        pxToMm,
      });
    }

    const pdfName = `PLANO - ${implantInfo.fullname} - ${formatDate(new Date(), 'short', 'es-AR')}`;

    await this.pdfGeneratorService.exportSvgToPdf(
      pdfName,
      { height: pixelesConvertFactorCol, width: pixelesConvertFactorRow },
      this.procedureTemplate(procedure_id, nussTemplateVariant),
      null,
      nussTemplateVariant
    );
  }

  async generateCorte(input: ImplantPdfInput): Promise<void> {
    const { curveData, implantInfo } = input;
    const pxToMm = curveData.conversionFactors.pixelesConvertFactorCol;
    const pixelesConvertFactorCol = curveData.conversionFactors.pixelesConvertFactorCol;
    const pixelesConvertFactorRow = curveData.conversionFactors.pixelesConvertFactorRow;

    this.pdfGeneratorCorteService.clearCurves();

    for (const layer of curveData.layers) {
      if (!layer.initialCurvePoints || layer.initialCurvePoints.length === 0) continue;

      const sfLengthPx = this.calculateBezierCurveLength(layer.initialCurvePoints);
      const sfLengthMm = this.convertPixelsToMillimeters(sfLengthPx, pxToMm);
      const implantLabel = this.getImplantTypeLabel(layer.name);
      const fullName = implantLabel
        ? `${implantInfo.fullname} - ${implantLabel}`
        : implantInfo.fullname;

      this.exportCurve({
        curvePoints: layer.initialCurvePoints,
        curveColor: 'red',
        curveTextColor: 'black',
        // Padding bajo (5mm) para que en el corte los N implantes queden
        // compactos y todos visibles en la página. Sincronizado con el
        // mismo valor en drawer.component.ts:exportCutCurves.
        canvasPaddingMm: 5,
        lines: [
          fullName,
          implantInfo.surgeryLocation,
          implantInfo.surgeryDay,
          `${abbreviateMaterial(implantInfo.material)} : ${Number(sfLengthMm).toFixed(2)} mm : SF 0/0 mm`,
        ],
        pdfService: this.pdfGeneratorCorteService,
        pxToMm,
      });
    }

    const pdfName = `CORTE - ${implantInfo.fullname} - ${formatDate(new Date(), 'short', 'es-AR')}`;

    await this.pdfGeneratorCorteService.exportSvgToPdf(
      pdfName,
      { height: pixelesConvertFactorRow, width: pixelesConvertFactorCol },
      'assets/CORTE LASER.pdf'
    );
  }

  private exportCurve(args: {
    curvePoints: ExportCurvePoint[];
    curveColor: string;
    curveTextColor: string;
    lines: string[];
    pdfService: { addCurve: (svg: string, text?: string[], color?: string) => void };
    canvasPaddingMm?: number;
    pxToMm: number;
  }): void {
    const {
      curvePoints,
      curveColor,
      lines,
      pdfService,
      canvasPaddingMm = 0,
      pxToMm: rawPxToMm,
    } = args;

    const pxToMm = rawPxToMm || 0.1;
    const fixedOffsetMm = -10;
    const fixedOffsetPx = fixedOffsetMm / pxToMm;

    const bezierPoints: Point[] = [];
    const displacedBezierPoints: Point[] = [];

    for (let i = 0; i < curvePoints.length - 1; i++) {
      const currentPoint = curvePoints[i];
      const nextPoint = curvePoints[i + 1];

      const cp1x = currentPoint.controlPoint2X !== undefined ? currentPoint.controlPoint2X : currentPoint.x;
      const cp1y = currentPoint.controlPoint2Y !== undefined ? currentPoint.controlPoint2Y : currentPoint.y;
      const cp2x = nextPoint.controlPoint1X !== undefined ? nextPoint.controlPoint1X : nextPoint.x;
      const cp2y = nextPoint.controlPoint1Y !== undefined ? nextPoint.controlPoint1Y : nextPoint.y;

      const samples = 200;
      for (let j = 0; j <= samples; j++) {
        const t = j / samples;
        const t1 = 1 - t;

        const x = t1*t1*t1*currentPoint.x + 3*t1*t1*t*cp1x + 3*t1*t*t*cp2x + t*t*t*nextPoint.x;
        const y = t1*t1*t1*currentPoint.y + 3*t1*t1*t*cp1y + 3*t1*t*t*cp2y + t*t*t*nextPoint.y;

        if (i > 0 && j === 0) continue;

        bezierPoints.push({ x, y });

        const dx = 3*t1*t1*(cp1x-currentPoint.x) + 6*t1*t*(cp2x-cp1x) + 3*t*t*(nextPoint.x-cp2x);
        const dy = 3*t1*t1*(cp1y-currentPoint.y) + 6*t1*t*(cp2y-cp1y) + 3*t*t*(nextPoint.y-cp2y);

        let nx = -dy;
        let ny = dx;

        const length = Math.sqrt(nx * nx + ny * ny);
        if (length > 0) {
          nx = nx / length;
          ny = ny / length;
        }

        const offsetX = nx * fixedOffsetPx;
        const offsetY = ny * fixedOffsetPx;

        displacedBezierPoints.push({
          x: x + offsetX,
          y: y + offsetY,
        });
      }
    }

    const baseInterpolatedCurve = bezierPoints;
    const displacedCurve = displacedBezierPoints;
    const tabResult = this.addTabsToDisplacedCurve(
      baseInterpolatedCurve,
      displacedCurve,
      pxToMm
    );
    const displacedCurveWithTab = tabResult.pointsWithTab;
    const puntoDeLaTabParaCartel = tabResult.puntoDeLaTabParaCartel;

    const allPoints = [...baseInterpolatedCurve, ...displacedCurveWithTab];

    const minX = Math.min(...allPoints.map(p => p.x));
    const maxX = Math.max(...allPoints.map(p => p.x));
    const minY = Math.min(...allPoints.map(p => p.y));
    const maxY = Math.max(...allPoints.map(p => p.y));

    const contentWidth = maxX - minX;
    const contentHeight = maxY - minY;
    const paddingPx = canvasPaddingMm > 0 && pxToMm > 0 ? canvasPaddingMm / pxToMm : 0;

    const temporalCanvas = document.createElement('canvas');
    temporalCanvas.width = Math.ceil(contentWidth + paddingPx * 2);
    temporalCanvas.height = Math.ceil(contentHeight + paddingPx * 2);

    const temporalCanvasContext: any = new C2S(
      temporalCanvas.width,
      temporalCanvas.height
    );

    if (!temporalCanvasContext) return;

    const offsetX = paddingPx - minX;
    const offsetY = paddingPx - minY;

    temporalCanvasContext.translate(offsetX, offsetY);

    temporalCanvasContext.beginPath();
    temporalCanvasContext.lineJoin = 'round';
    temporalCanvasContext.lineCap = 'round';

    if (curvePoints.length > 0 && displacedCurveWithTab.length > 0) {
      temporalCanvasContext.moveTo(curvePoints[0].x, curvePoints[0].y);
      for (let i = 0; i < curvePoints.length - 1; i++) {
        const currentPoint = curvePoints[i];
        const nextPoint = curvePoints[i + 1];
        const cp1x = currentPoint.controlPoint2X !== undefined ? currentPoint.controlPoint2X : currentPoint.x;
        const cp1y = currentPoint.controlPoint2Y !== undefined ? currentPoint.controlPoint2Y : currentPoint.y;
        const cp2x = nextPoint.controlPoint1X !== undefined ? nextPoint.controlPoint1X : nextPoint.x;
        const cp2y = nextPoint.controlPoint1Y !== undefined ? nextPoint.controlPoint1Y : nextPoint.y;
        temporalCanvasContext.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, nextPoint.x, nextPoint.y);
      }

      const lastDisplaced = displacedCurveWithTab[displacedCurveWithTab.length - 1];
      temporalCanvasContext.lineTo(lastDisplaced.x, lastDisplaced.y);

      for (let i = displacedCurveWithTab.length - 2; i >= 0; i--) {
        temporalCanvasContext.lineTo(displacedCurveWithTab[i].x, displacedCurveWithTab[i].y);
      }

      temporalCanvasContext.closePath();
    }

    temporalCanvasContext.strokeStyle = curveColor;
    temporalCanvasContext.lineWidth = 0.8;
    temporalCanvasContext.stroke();

    this.createSolapaText(temporalCanvasContext, curveColor, lines, puntoDeLaTabParaCartel);

    const curveSVG = temporalCanvasContext.getSerializedSvg();
    const innerStart = curvePoints[0];
    const innerEnd = curvePoints[curvePoints.length - 1];
    const innerStartX = innerStart.x + offsetX;
    const innerStartY = innerStart.y + offsetY;
    const innerEndX = innerEnd.x + offsetX;
    const innerEndY = innerEnd.y + offsetY;

    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(curveSVG, 'image/svg+xml');
    const svgRoot = svgDoc.documentElement;
    svgRoot.setAttribute('data-inner-start-x', innerStartX.toString());
    svgRoot.setAttribute('data-inner-start-y', innerStartY.toString());
    svgRoot.setAttribute('data-inner-end-x', innerEndX.toString());
    svgRoot.setAttribute('data-inner-end-y', innerEndY.toString());
    const serializedSvg = new XMLSerializer().serializeToString(svgRoot);

    pdfService.addCurve(serializedSvg, lines, curveColor);
  }

  private addTabsToDisplacedCurve(
    baseCurve: Point[],
    displacedCurve: Point[],
    pxToMm: number
  ): { pointsWithTab: Point[]; puntoDeLaTabParaCartel: TabCornerPoints | null } {
    if (!pxToMm || pxToMm <= 0) {
      return { pointsWithTab: displacedCurve, puntoDeLaTabParaCartel: null };
    }
    if (
      !baseCurve ||
      !displacedCurve ||
      baseCurve.length === 0 ||
      displacedCurve.length === 0 ||
      baseCurve.length !== displacedCurve.length
    ) {
      return { pointsWithTab: displacedCurve, puntoDeLaTabParaCartel: null };
    }

    const xs = displacedCurve.map(p => p.x);
    const ys = displacedCurve.map(p => p.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    const rangeX = maxX - minX;
    const rangeY = maxY - minY;
    const useX = rangeX >= rangeY;
    const axisMin = useX ? minX : minY;
    const axisMax = useX ? maxX : maxY;
    const axisRange = axisMax - axisMin;
    if (axisRange <= 0) return { pointsWithTab: displacedCurve, puntoDeLaTabParaCartel: null };

    const tabCenterOffsetRatio = 0.5;
    const tabHeightRatio = 0.40;
    const extraTabMm = 20;
    const tabDepthMm = 45;
    const tabOuterShrinkRatio = 0.22;
    const tabHeightPx = Math.min(
      axisRange,
      axisRange * tabHeightRatio + extraTabMm / pxToMm
    );
    const tabDepthPx = tabDepthMm / pxToMm;

    const tabCenterAxis = useX
      ? axisMax - axisRange * tabCenterOffsetRatio
      : axisMin + axisRange * tabCenterOffsetRatio;
    const tabStartAxis = useX
      ? tabCenterAxis + tabHeightPx / 2
      : tabCenterAxis - tabHeightPx / 2;
    const tabEndAxis = useX
      ? tabCenterAxis - tabHeightPx / 2
      : tabCenterAxis + tabHeightPx / 2;

    const findClosestIndex = (targetAxis: number) => {
      let bestIndex = 0;
      const firstAxis = useX ? displacedCurve[0].x : displacedCurve[0].y;
      let bestDist = Math.abs(firstAxis - targetAxis);
      for (let i = 1; i < displacedCurve.length; i++) {
        const axisValue = useX ? displacedCurve[i].x : displacedCurve[i].y;
        const dist = Math.abs(axisValue - targetAxis);
        if (dist < bestDist) {
          bestDist = dist;
          bestIndex = i;
        }
      }
      return bestIndex;
    };

    const outwardUnit = (i: number) => {
      const base = baseCurve[i];
      const disp = displacedCurve[i];
      let vx = disp.x - base.x;
      let vy = disp.y - base.y;
      const len = Math.hypot(vx, vy) || 1;
      vx /= len;
      vy /= len;
      return { x: vx, y: vy };
    };

    let puntoDeLaTabParaCartel: TabCornerPoints | null = null;

    const buildTabRange = (startAxis: number, endAxis: number) => {
      let startIndex = findClosestIndex(startAxis);
      let endIndex = findClosestIndex(endAxis);
      if (startIndex === endIndex) return null;
      if (startIndex > endIndex) {
        const tmp = startIndex;
        startIndex = endIndex;
        endIndex = tmp;
      }
      if (endIndex - startIndex < 2) return null;

      const start = displacedCurve[startIndex];
      const end = displacedCurve[endIndex];
      const startOut = outwardUnit(startIndex);
      const endOut = outwardUnit(endIndex);

      const startTab: Point = {
        x: start.x + startOut.x * tabDepthPx,
        y: start.y + startOut.y * tabDepthPx,
      };
      const endTab: Point = {
        x: end.x + endOut.x * tabDepthPx,
        y: end.y + endOut.y * tabDepthPx,
      };

      const dx = end.x - start.x;
      const dy = end.y - start.y;
      const edgeLen = Math.hypot(dx, dy) || 1;
      const edgeDir = { x: dx / edgeLen, y: dy / edgeLen };
      const outerShrink = edgeLen * tabOuterShrinkRatio;

      const outerStart: Point = {
        x: startTab.x + edgeDir.x * outerShrink,
        y: startTab.y + edgeDir.y * outerShrink,
      };
      const outerEnd: Point = {
        x: endTab.x - edgeDir.x * outerShrink,
        y: endTab.y - edgeDir.y * outerShrink,
      };

      // Guardamos también las esquinas externas reales (no solo start/end +
      // vectores unitarios) para que createSolapaText ubique el texto en el
      // centro real del trapecio, igual que el drawer.
      puntoDeLaTabParaCartel = { start, end, startOut, endOut, outerStart, outerEnd };

      return { startIndex, endIndex, start, end, outerStart, outerEnd };
    };

    const range = buildTabRange(tabStartAxis, tabEndAxis);
    if (!range) return { pointsWithTab: displacedCurve, puntoDeLaTabParaCartel };

    const withTab: Point[] = [];
    for (let i = 0; i < displacedCurve.length; i++) {
      if (i === range.startIndex) {
        withTab.push(range.start);
        withTab.push(range.outerStart);
        i = range.endIndex;
        withTab.push(range.outerEnd);
        withTab.push(range.end);
        continue;
      }
      if (i > range.startIndex && i < range.endIndex) {
        continue;
      }
      withTab.push(displacedCurve[i]);
    }

    return { pointsWithTab: withTab, puntoDeLaTabParaCartel };
  }

  /**
   * Ubica el texto del cartel dentro de la solapa. Portado 1:1 desde
   * DrawerComponent.createSolapaText (el visualizador ya tenía el fix del Ítem O;
   * esta copia headless había quedado desactualizada, con el texto colapsado
   * sobre el borde interno por reconstruir el fondo con offsets hardcodeados).
   *
   * Usa el trapecio REAL de la solapa (start/end + outerStart/outerEnd), centra
   * el texto en su centroide, lo ROTA al ángulo del borde interno y AUTO-AJUSTA
   * la fuente para que entre en la profundidad/ancho disponibles.
   */
  private createSolapaText(
    ctx: any,
    color: string,
    lines: string[],
    puntoDeLaTabParaCartel: TabCornerPoints | null,
    fontSize: number = 7
  ): void {
    if (!puntoDeLaTabParaCartel) return;
    if (lines.length === 0) return;

    const { start, end, outerStart, outerEnd } = puntoDeLaTabParaCartel;

    // Centroide del trapecio real de la solapa (start, end, outerEnd, outerStart).
    const cx = (start.x + end.x + outerStart.x + outerEnd.x) / 4;
    const cy = (start.y + end.y + outerStart.y + outerEnd.y) / 4;

    // El texto SIGUE la inclinación del borde interno (start→end).
    const ex = end.x - start.x;
    const ey = end.y - start.y;
    const edgeLenInner = Math.hypot(ex, ey) || 1;
    let angle = Math.atan2(ey, ex);
    // Plegar a [-90°, 90°] para que el texto nunca quede invertido.
    if (angle > Math.PI / 2) angle -= Math.PI;
    else if (angle < -Math.PI / 2) angle += Math.PI;

    // Profundidad de la solapa (borde interno → externo) para acotar el alto.
    const innerMidX = (start.x + end.x) / 2;
    const innerMidY = (start.y + end.y) / 2;
    const outerMidX = (outerStart.x + outerEnd.x) / 2;
    const outerMidY = (outerStart.y + outerEnd.y) / 2;
    const depthLen = Math.hypot(outerMidX - innerMidX, outerMidY - innerMidY) || 1;

    // Anchos disponibles en el marco de la solapa: a lo largo del borde ≈ ancho,
    // profundidad ≈ alto (promedio de borde interno/externo como clearance).
    const edgeLenOuter = Math.hypot(outerEnd.x - outerStart.x, outerEnd.y - outerStart.y);
    const margin = 6;
    const availAlong = Math.max(1, (edgeLenInner + edgeLenOuter) / 2 - 2 * margin);
    const availDepth = Math.max(1, depthLen - 2 * margin);

    // Auto-ajuste de fuente: preferimos `fontSize` (7px) y sólo reducimos si el
    // bloque no entra. Piso 4px para legibilidad/grabado.
    const MIN_FONT = 4;
    ctx.font = `bold ${fontSize}px sans-serif`;
    let blockW = 0;
    for (const line of lines) {
      const w = ctx.measureText(line).width;
      if (w > blockW) blockW = w;
    }
    const blockH = lines.length * fontSize * 1.2;
    const scale = Math.min(1, availAlong / blockW, availDepth / blockH);
    const finalFont = Math.max(MIN_FONT, fontSize * scale);

    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(angle);
    ctx.font = `bold ${finalFont}px sans-serif`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = color;

    // Alineado a la izquierda: todas las líneas arrancan en el mismo x, con el
    // bloque centrado en la solapa.
    let finalMaxWidth = 0;
    for (const line of lines) {
      const w = ctx.measureText(line).width;
      if (w > finalMaxWidth) finalMaxWidth = w;
    }
    const leftX = -finalMaxWidth / 2;

    const lineHeight = finalFont * 1.2;
    for (let i = 0; i < lines.length; i++) {
      const localY = (i - (lines.length - 1) / 2) * lineHeight;
      ctx.fillText(lines[i], leftX, localY);
    }

    ctx.restore();
  }

  private procedureTemplate(id: number, nussTemplateVariant: 'v1' | 'v2'): string {
    const factoryProcedure: IProcedureTemplate[] = [
      { id: 5, templateUrl: 'assets/sandwich interno.pdf' },
      { id: 3, templateUrl: 'assets/nuss acero.pdf' },
      { id: 6, templateUrl: 'assets/sandwich externo.pdf' },
      { id: 8, templateUrl: 'assets/zip back.pdf' },
    ];
    if (id === 3 && nussTemplateVariant === 'v2') {
      return 'assets/nuss titanio.pdf';
    }
    return factoryProcedure.find((p) => p.id === id)?.templateUrl || '';
  }

  private getImplantTypeLabel(name: string): string {
    const normalized = (name || '').toLowerCase();
    // TC-PDF-014: matchear combinaciones primero. Si no, "MEDIO INFERIOR"
    // colisionaba con INFERIOR y "MEDIO SUPERIOR" se mezclaba con MEDIO solo.
    // Mantener esta función en sync con la copia en drawer.component.ts.
    const hasMedio = normalized.includes('medio') || normalized.includes('med');
    const hasSuperior = normalized.includes('superior') || normalized.includes('sup');
    const hasInferior = normalized.includes('inferior') || normalized.includes('inf');
    if (hasMedio && hasSuperior) return 'MED SUP';
    if (hasMedio && hasInferior) return 'MED INF';
    if (hasInferior) return 'INF';
    if (hasMedio) return 'MED';
    if (hasSuperior) return 'SUP';
    if (normalized.includes('externo') || normalized.includes('ext')) return 'EXT';
    return '';
  }

  private calculateBezierCurveLength(points: ExportCurvePoint[]): number {
    if (!points || points.length < 2) return 0;

    let totalLength = 0;
    for (let i = 0; i < points.length - 1; i++) {
      const currentPoint = points[i];
      const nextPoint = points[i + 1];
      const cp1x = currentPoint.controlPoint2X !== undefined ? currentPoint.controlPoint2X : currentPoint.x;
      const cp1y = currentPoint.controlPoint2Y !== undefined ? currentPoint.controlPoint2Y : currentPoint.y;
      const cp2x = nextPoint.controlPoint1X !== undefined ? nextPoint.controlPoint1X : nextPoint.x;
      const cp2y = nextPoint.controlPoint1Y !== undefined ? nextPoint.controlPoint1Y : nextPoint.y;

      totalLength += this.approximateBezierLength(
        currentPoint.x, currentPoint.y,
        cp1x, cp1y,
        cp2x, cp2y,
        nextPoint.x, nextPoint.y,
        50
      );
    }
    return totalLength;
  }

  private approximateBezierLength(
    x0: number, y0: number,
    x1: number, y1: number,
    x2: number, y2: number,
    x3: number, y3: number,
    samples: number
  ): number {
    let length = 0;
    let prevX = x0;
    let prevY = y0;

    for (let i = 1; i <= samples; i++) {
      const t = i / samples;
      const t1 = 1 - t;

      const x = t1*t1*t1*x0 + 3*t1*t1*t*x1 + 3*t1*t*t*x2 + t*t*t*x3;
      const y = t1*t1*t1*y0 + 3*t1*t1*t*y1 + 3*t1*t*t*y2 + t*t*t*y3;

      const dx = x - prevX;
      const dy = y - prevY;
      length += Math.sqrt(dx*dx + dy*dy);

      prevX = x;
      prevY = y;
    }

    return length;
  }

  private convertPixelsToMillimeters(pixels: number, pxToMm: number): number {
    const mm = pixels * pxToMm;
    return parseFloat(mm.toFixed(2));
  }
}
